import { useContext, useEffect, useMemo, useRef, useState } from "react";

import { ButtonsRight } from "../../../../interfaces";
import { PublicationContext } from "../../../../context/PublicationContext";
import { ReviewCard } from "./ReviewCard";
import { useUser } from "../../../../hooks";
import { usePublication } from "../../../../hooks/usePublication";

interface Review {
  fvu_id: number;
  fvu_practica: string;
  fvu_nombre_usuario: string;
  fvu_email: string;
  fvu_id_usuario: string;
  fvu_institucion: string;
  fvu_calificacion: number;
  fvu_opinion: string;
  fvu_fecha_valoracion: string;
  fvu_tituloReview: string | null;
}

interface Props {
  openButtons: ButtonsRight;
  setOpenButtons: (button: ButtonsRight) => void;
}

export const ReviewsComponent = ({ openButtons, setOpenButtons }: Props) => {
  const [openCalificacion, setOpenCalificacion] = useState<Boolean>(false);
  const [filterRating, setFilterRating] = useState<number | null>(null);
  const [selectedRating, setSelectedRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [textReview, setTextReview] = useState<string>("");
  const [titleReview, setTitleReview] = useState<string>("");
  const [isSendReview, setIsSendReview] = useState<boolean>(false);
  const [alertError, setAlertError] = useState<string>("");
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const { user } = useUser();
  const { publication } = usePublication();

  // Obtener reviews desde la API
  useEffect(() => {
    const fetchReviews = async () => {
      if (!publication?.urlVirtualpro) return;

      try {
        setLoading(true);
        const url = publication.urlVirtualpro;
        const libroId = url.split("-").pop();

        const response = await fetch(
          `https://api.virtualpro.co/api/cliente/reviews_libros?libro_id=${libroId}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();

        if (Array.isArray(data)) {
          setReviews(data);
        } else {
          setReviews([]);
        }
      } catch (error) {
        console.error("Error fetching reviews:", error);
        setReviews([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [publication?.urlVirtualpro]);

  // Filtrar reviews según el rating seleccionado
  const filteredReviews = useMemo(() => {
    if (filterRating === null) return reviews;
    return reviews.filter((review) => review.fvu_calificacion === filterRating);
  }, [reviews, filterRating]);

  // Calcular estadísticas
  const reviewStats = useMemo(() => {
    const totalReviews = reviews.length;
    if (totalReviews === 0) {
      return {
        totalReviews: 0,
        averageRating: 0,
        ratingCounts: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
        ratingPercentages: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
      };
    }

    const averageRating =
      reviews.reduce((sum, review) => sum + review.fvu_calificacion, 0) /
      totalReviews;

    // Contar reviews por rating
    const ratingCounts: { [key: number]: number } = {
      5: 0,
      4: 0,
      3: 0,
      2: 0,
      1: 0,
    };

    reviews.forEach((review) => {
      const rating = review.fvu_calificacion;
      if (rating >= 1 && rating <= 5) {
        ratingCounts[rating]++;
      }
    });

    // Calcular porcentajes para las barras
    const ratingPercentages = {
      5: (ratingCounts[5] / totalReviews) * 100,
      4: (ratingCounts[4] / totalReviews) * 100,
      3: (ratingCounts[3] / totalReviews) * 100,
      2: (ratingCounts[2] / totalReviews) * 100,
      1: (ratingCounts[1] / totalReviews) * 100,
    };

    return {
      totalReviews,
      averageRating: Math.round(averageRating * 10) / 10,
      ratingCounts,
      ratingPercentages,
    };
  }, [reviews]);

  // Función para manejar el filtro por rating
  const handleRatingFilter = (rating: number) => {
    if (filterRating === rating) {
      setFilterRating(null);
    } else {
      setFilterRating(rating);
    }
  };

  const handleReviewSubmit = async () => {
    if (!user) {
      setAlertError("Debes iniciar sesión para calificar.");
      return;
    }

    if (selectedRating === 0) {
      setAlertError("Por favor califica antes de enviar.");
      return;
    }

    setAlertError("");
    setIsSendReview(true);

    const url = publication.urlVirtualpro;
    let idLibro;

    if (url) {
      idLibro = url.split("-").pop();
    }

    try {
      const data = await fetch(
        "https://apivirtualplant.com/apiVpro/public/api/saveValoracionUnity",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            fvu_practica: idLibro,
            fvu_nombre_usuario: user?.sus_nombre,
            fvu_email: user?.sus_email,
            fvu_id_usuario: user?.sus_id,
            fvu_institucion: "",
            fvu_calificacion: selectedRating,
            fvu_opinion: textReview,
            fvu_tituloReview: titleReview,
          }),
        }
      ).then((res) => res.json());

      if (data.error) {
        setAlertError("Error al enviar la calificación. Intenta nuevamente.");
        return;
      }

      if (!publication?.urlVirtualpro) return;

      const url = publication.urlVirtualpro;
      const libroId = url.split("-").pop();
      
      // Recargar las reviews después de enviar
      const response = await fetch(
        `https://api.virtualpro.co/api/cliente/reviews_libros?libro_id=${libroId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const newReviews = await response.json();
      if (Array.isArray(newReviews)) {
        setReviews(newReviews);
      }

      setTimeout(() => {
        setOpenCalificacion(false);
        setIsSendReview(false);
        setSelectedRating(0);
        setTextReview("");
      }, 2000);
    } catch (error) {
      setAlertError("Error de conexión. Intenta nuevamente.");
    }
  };

  // Función para formatear la fecha
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div
      id="option-5"
      className={`aside__content ${openButtons.optReviews ? "active" : ""}`}
    >
      <div className="aside__content-header">
        <h3>Reviews</h3>
        <button
          className="btn btn-rounded btn-rounded-transparent btn-close"
          onClick={() => {
            setOpenButtons({ ...openButtons, optReviews: false });
          }}
        >
          <span className="btn__icon">
            <i className="icon icon-vpro-close"></i>
          </span>
        </button>
      </div>

      <div
        className={`aside__content-data main-sb ${
          openCalificacion ? "fadeon" : ""
        }`}
      >
        <div className="review-grp" data-fadec="fade-2">
          {loading ? (
            <div
              style={{ textAlign: "center", padding: "20px", color: "#666" }}
            >
              Cargando reseñas...
            </div>
          ) : (
            <>
              <div className="review-grp__rating">
                <div className="review-grp__rating-item">
                  {/* Renderizar estadísticas para cada rating */}
                  {[5, 4, 3, 2, 1].map((rating) => (
                    <a
                      key={rating}
                      href="#"
                      className={`review-grp__rating-stats ${
                        filterRating === rating ? "active" : ""
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleRatingFilter(rating);
                      }}
                    >
                      <div className="review-rating">
                        <div className="review-rating__grp">
                          {Array.from({ length: 5 }, (_, index) => (
                            <span
                              key={index}
                              className={`review-rating__icon ${
                                index < rating
                                  ? "review-rating__icon--full"
                                  : ""
                              }`}
                            >
                              <i
                                className={`icon ${
                                  index < rating ? "icon-vpro-star-filled" : "icon-vpro-star-outline"
                                }`}
                              ></i>
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="review-bars">
                        <div className="prg-bar prg-bar--rounded prg-bar--sm">
                          <div
                            className="prg-bar__progress prg-bar__progress--blue"
                            style={{
                              width: `${
                                reviewStats.ratingPercentages[
                                  rating as keyof typeof reviewStats.ratingPercentages
                                ]
                              }%`,
                            }}
                          ></div>
                        </div>
                      </div>
                      <div className="review-counter">
                        ({reviewStats.ratingCounts[rating]})
                      </div>
                    </a>
                  ))}
                </div>
                <div className="review-grp__rating-score">
                  <span className="review-rating__number">
                    {reviewStats.averageRating}
                  </span>
                  <div className="review-rating__counter">
                    ({reviewStats.totalReviews})
                  </div>
                  {filterRating && (
                    <button
                      onClick={() => setFilterRating(null)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#666",
                        fontSize: "12px",
                        cursor: "pointer",
                        marginTop: "5px",
                        textDecoration: "underline",
                      }}
                    >
                      Quitar filtro
                    </button>
                  )}
                </div>
              </div>
              <div className="review-grp__list">
                {filteredReviews.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: "#666",
                    }}
                  >
                    {reviews.length === 0
                      ? "Aún no hay reseñas disponibles"
                      : "No hay reseñas con esta calificación"}
                  </div>
                ) : (
                  filteredReviews.map((review) => (
                    <ReviewCard
                      key={review.fvu_id}
                      title={
                        review.fvu_tituloReview ||
                        `Calificación ${review.fvu_calificacion} estrellas`
                      }
                      description={review.fvu_opinion}
                      date={formatDate(review.fvu_fecha_valoracion)}
                      rating={review.fvu_calificacion}
                      author={review.fvu_nombre_usuario}
                    />
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* El resto del código del formulario de calificación se mantiene igual */}
      <div className="aside__content-actions">
        <div className="sub-content">
          <div
            id="subc-2"
            className={`sub-content__content ${
              openCalificacion ? "xpand" : ""
            }`}
          >
            <div className="sub-content__actions">
              <button
                type="button"
                className="sub-button"
                data-subc="subc-2"
                aria-label="Cerrar venta de calificación"
                onClick={() => {
                  setOpenCalificacion(false);
                }}
              >
                <span className="sub-button__icon">
                  <i className="icon icon-vpro-chevron-down"></i>
                </span>
              </button>
            </div>
            <div className="rating">
              {/* Estrellas con efecto hover */}
              <span
                className={`rating__icon ${
                  hoverRating >= 1 || selectedRating >= 1
                    ? "rating__icon--full"
                    : ""
                }`}
                onClick={() => setSelectedRating(1)}
                onMouseEnter={() => setHoverRating(1)}
                onMouseLeave={() => setHoverRating(0)}
                style={{ cursor: "pointer" }}
              >
                <i
                  className={`icon ${
                    hoverRating >= 1 || selectedRating >= 1
                      ? "icon-vpro-star-filled"
                      : "icon-vpro-star-outline"
                  }`}
                ></i>
              </span>
              <span
                className={`rating__icon ${
                  hoverRating >= 2 || selectedRating >= 2
                    ? "rating__icon--full"
                    : ""
                }`}
                onClick={() => setSelectedRating(2)}
                onMouseEnter={() => setHoverRating(2)}
                onMouseLeave={() => setHoverRating(0)}
                style={{ cursor: "pointer" }}
              >
                <i
                  className={`icon ${
                    hoverRating >= 2 || selectedRating >= 2
                      ? "icon-vpro-star-filled"
                      : "icon-vpro-star-outline"
                  }`}
                ></i>
              </span>
              <span
                className={`rating__icon ${
                  hoverRating >= 3 || selectedRating >= 3
                    ? "rating__icon--full"
                    : ""
                }`}
                onClick={() => setSelectedRating(3)}
                onMouseEnter={() => setHoverRating(3)}
                onMouseLeave={() => setHoverRating(0)}
                style={{ cursor: "pointer" }}
              >
                <i
                  className={`icon ${
                    hoverRating >= 3 || selectedRating >= 3
                      ? "icon-vpro-star-filled"
                      : "icon-vpro-star-outline"
                  }`}
                ></i>
              </span>
              <span
                className={`rating__icon ${
                  hoverRating >= 4 || selectedRating >= 4
                    ? "rating__icon--full"
                    : ""
                }`}
                onClick={() => setSelectedRating(4)}
                onMouseEnter={() => setHoverRating(4)}
                onMouseLeave={() => setHoverRating(0)}
                style={{ cursor: "pointer" }}
              >
                <i
                  className={`icon ${
                    hoverRating >= 4 || selectedRating >= 4
                      ? "icon-vpro-star-filled"
                      : "icon-vpro-star-outline"
                  }`}
                ></i>
              </span>
              <span
                className={`rating__icon ${
                  hoverRating >= 5 || selectedRating >= 5
                    ? "rating__icon--full"
                    : ""
                }`}
                onClick={() => setSelectedRating(5)}
                onMouseEnter={() => setHoverRating(5)}
                onMouseLeave={() => setHoverRating(0)}
                style={{ cursor: "pointer" }}
              >
                <i
                  className={`icon ${
                    hoverRating >= 5 || selectedRating >= 5
                      ? "icon-vpro-star-filled"
                      : "icon-vpro-star-outline"
                  }`}
                ></i>
              </span>
            </div>
            <textarea
              name="Rate"
              id="txtAreaTitle"
              className="txt-area"
              cols={30}
              rows={1.1}
              value={titleReview}
              onChange={(e) => setTitleReview(e.target.value)}
              placeholder="Título de la reseña"
            ></textarea>
            <textarea
              name="Rate"
              id="txtAreaRate"
              className="txt-area"
              cols={30}
              rows={10}
              value={textReview}
              onChange={(e) => setTextReview(e.target.value)}
              placeholder="Califica este libro"
            ></textarea>
            {alertError && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#F18701",
                  color: "#fff",
                  padding: "10px 13px",
                  borderRadius: "10px",
                  fontWeight: "700",
                  fontSize: "14px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  width: "100%",
                }}
              >
                <span
                  className="btn__icon"
                  style={{
                    marginRight: "10px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <i
                    className="icon icon-vpro-oc-info"
                    style={{ fontSize: "18px", marginTop: "-1px" }}
                  ></i>
                </span>
                <span>{alertError}</span>
              </div>
            )}
            <button
              type="button"
              className={`btn btn-alt-secondary btn--fw ${
                isSendReview ? "sent" : ""
              }`}
              data-subc="subc-2"
              onClick={() => {
                handleReviewSubmit();
              }}
            >
              <span className="btn__label">
                {isSendReview ? "Calificación enviada" : "Calificar"}
              </span>
            </button>
          </div>
        </div>
        <button
          type="button"
          className={`btn btn-alt-primary btn-alt-primary--red btn--fw sub-button ${
            openCalificacion ? "selected" : ""
          }`}
          data-subc="subc-2"
          onClick={() => {
            setOpenCalificacion(!openCalificacion);
          }}
        >
          <span className="btn__label">Danos tu opinión</span>
        </button>
      </div>
    </div>
  );
};
